import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Stage, Layer, Rect, Circle, Line, Text, Group, Arrow, Image as KonvaImage } from 'react-konva';
import { ArrowLeft, Save, Upload, ZoomIn, ZoomOut, Maximize, Trash2, RotateCw, FileDown, Eye, EyeOff, Crosshair, X, Check, Truck, MousePointer, Route } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCALES = ['1:50', '1:100', '1:200', '1:500'];

const VehicleAccessPlanner = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  // Canvas state
  const stageRef = useRef(null);
  const containerRef = useRef(null);
  const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  // Project data
  const [projectName, setProjectName] = useState('');
  const [siteWidthMm, setSiteWidthMm] = useState('');
  const [siteDepthMm, setSiteDepthMm] = useState('');
  const [siteScale, setSiteScale] = useState('1:200');
  const [planImage, setPlanImage] = useState(null);
  const [planImageObj, setPlanImageObj] = useState(null);

  // Vehicle
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const selectedVehicle = useMemo(() => vehicles.find(v => v.id === selectedVehicleId), [vehicles, selectedVehicleId]);

  // Waypoints & path
  const [waypoints, setWaypoints] = useState([]);
  const [pathMode, setPathMode] = useState(false); // click-to-add waypoints
  const [selectedWaypointIdx, setSelectedWaypointIdx] = useState(null);

  // Calibration
  const [calibrateMode, setCalibrateMode] = useState(false);
  const [calibratePoints, setCalibratePoints] = useState([]);
  const [calibrateDistanceMm, setCalibrateDistanceMm] = useState('');
  const [calibrationData, setCalibrationData] = useState(null);

  // UI state
  const [showSetup, setShowSetup] = useState(false);
  const [showUpload, setShowUpload] = useState(true);
  const [showEnvelope, setShowEnvelope] = useState(true);
  const [saving, setSaving] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [analysisFlags, setAnalysisFlags] = useState([]);
  const fileInputRef = useRef(null);

  const pxPerMm = useMemo(() => {
    if (calibrationData?.pxPerMm) return calibrationData.pxPerMm;
    const scaleNum = parseInt(siteScale.split(':')[1]) || 200;
    return 1 / scaleNum * 3.78;
  }, [siteScale, calibrationData]);

  // Load project + vehicles
  useEffect(() => {
    const load = async () => {
      try {
        const [projRes, vehRes] = await Promise.all([
          axios.get(`${API}/vehicle-access/${projectId}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/vehicles`),
        ]);
        const d = projRes.data;
        setProjectName(d.name || '');
        setSiteWidthMm(d.site_width_mm || '');
        setSiteDepthMm(d.site_depth_mm || '');
        setSiteScale(d.scale || '1:200');
        setSelectedVehicleId(d.selected_vehicle_id || null);
        setWaypoints(d.waypoints || []);
        setAnalysisFlags(d.analysis_results || []);
        if (d.canvas_state?.calibration) setCalibrationData(d.canvas_state.calibration);
        if (d.plan_image_data) {
          const img = new window.Image();
          img.src = d.plan_image_data;
          img.onload = () => { setPlanImageObj(img); setPlanImage(d.plan_image_data); };
          setShowUpload(false);
        } else if (d.waypoints?.length > 0 || d.site_width_mm) {
          setShowUpload(false);
        }
        setVehicles(vehRes.data);
      } catch (e) { console.error(e); }
    };
    load();
  }, [projectId, token]);

  // Resize
  useEffect(() => {
    const resize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setStageSize({ width: rect.width, height: rect.height });
      }
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  // Drawing dimensions
  const roomWPx = siteWidthMm ? parseFloat(siteWidthMm) * pxPerMm : 0;
  const roomHPx = siteDepthMm ? parseFloat(siteDepthMm) * pxPerMm : 0;

  // File upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/vehicle-access/${projectId}/upload-plan`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new window.Image();
        img.src = ev.target.result;
        img.onload = () => { setPlanImageObj(img); setPlanImage(ev.target.result); };
      };
      reader.readAsDataURL(file);
      setShowUpload(false);
    } catch (err) { console.error(err); }
  };

  // Save
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/vehicle-access/${projectId}`, {
        site_width_mm: parseFloat(siteWidthMm) || null,
        site_depth_mm: parseFloat(siteDepthMm) || null,
        scale: siteScale,
        selected_vehicle_id: selectedVehicleId,
        waypoints,
        canvas_state: { calibration: calibrationData },
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  // Analyse
  const handleAnalyse = async () => {
    await handleSave();
    setAnalysing(true);
    try {
      const { data } = await axios.post(`${API}/vehicle-access/${projectId}/analyse`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setAnalysisFlags(data.flags || []);
    } catch (e) { console.error(e); }
    setAnalysing(false);
  };

  // Export PDF with canvas snapshot
  const handleExportPdf = async () => {
    await handleSave();
    try {
      let canvasImage = null;
      if (stageRef.current) {
        const stage = stageRef.current;
        const oldScale = stage.scaleX();
        const oldPos = stage.position();

        // Calculate bounding box of all content
        let minX = 0, minY = 0, maxX = stageSize.width, maxY = stageSize.height;

        // Include background image bounds
        if (planImageObj) {
          const imgW = roomWPx > 0 ? roomWPx : planImageObj.width;
          const imgH = roomHPx > 0 ? roomHPx : planImageObj.height;
          maxX = Math.max(maxX, imgW);
          maxY = Math.max(maxY, imgH);
        }

        // Include waypoints
        waypoints.forEach(wp => {
          const px = wp.x_mm * pxPerMm;
          const py = wp.y_mm * pxPerMm;
          minX = Math.min(minX, px - 200);
          minY = Math.min(minY, py - 200);
          maxX = Math.max(maxX, px + 200);
          maxY = Math.max(maxY, py + 200);
        });

        // Include site boundary
        if (roomWPx > 0) maxX = Math.max(maxX, roomWPx);
        if (roomHPx > 0) maxY = Math.max(maxY, roomHPx);

        const contentW = maxX - minX;
        const contentH = maxY - minY;

        // Capture at a scale that fits reasonably (max 4000px wide for quality)
        const captureScale = Math.min(2, 4000 / contentW, 4000 / contentH);

        stage.scale({ x: 1, y: 1 });
        stage.position({ x: 0, y: 0 });
        stage.batchDraw();

        canvasImage = stage.toDataURL({
          x: minX,
          y: minY,
          width: contentW,
          height: contentH,
          pixelRatio: captureScale,
          mimeType: 'image/png',
        });

        stage.scale({ x: oldScale, y: oldScale });
        stage.position(oldPos);
        stage.batchDraw();
      }
      const res = await axios.post(`${API}/vehicle-access/${projectId}/export-pdf`,
        { canvas_image: canvasImage },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `VehicleAccess_${projectName.replace(/\s/g, '_')}.pdf`;
      link.click();
    } catch (e) { console.error(e); }
  };

  // Zoom
  const handleWheel = useCallback((e) => {
    e.evt.preventDefault();
    const scaleBy = 1.08;
    const stage = stageRef.current;
    const pointer = stage.getPointerPosition();
    const oldScale = scale;
    const newScale = e.evt.deltaY < 0 ? oldScale * scaleBy : oldScale / scaleBy;
    const clampedScale = Math.max(0.1, Math.min(10, newScale));
    setScale(clampedScale);
    setPosition({
      x: pointer.x - (pointer.x - position.x) * (clampedScale / oldScale),
      y: pointer.y - (pointer.y - position.y) * (clampedScale / oldScale),
    });
  }, [scale, position]);

  const zoomIn = () => setScale(s => Math.min(10, s * 1.3));
  const zoomOut = () => setScale(s => Math.max(0.1, s / 1.3));
  const resetView = () => { setScale(1); setPosition({ x: 0, y: 0 }); };

  // Canvas click — add waypoints or calibrate
  const handleStageClick = (e) => {
    // Allow clicks on Stage, background layers, images, and rects (non-interactive elements)
    const target = e.target;
    const cn = target.getClassName ? target.getClassName() : '';
    const isBackground = target === stageRef.current || cn === 'Image' || cn === 'Rect' || cn === 'Layer';
    if (!isBackground) return;

    const stage = stageRef.current;
    const pointer = stage.getPointerPosition();
    const pxX = (pointer.x - position.x) / scale;
    const pxY = (pointer.y - position.y) / scale;

    if (calibrateMode) {
      if (calibratePoints.length < 2) {
        setCalibratePoints(prev => [...prev, { x: pxX, y: pxY }]);
      } else {
        setCalibratePoints([{ x: pxX, y: pxY }]);
      }
      return;
    }

    if (pathMode) {
      const mmX = pxX / pxPerMm;
      const mmY = pxY / pxPerMm;
      setWaypoints(prev => [...prev, { x_mm: Math.round(mmX), y_mm: Math.round(mmY), reverse: false }]);
      setSelectedWaypointIdx(waypoints.length);
      return;
    }

    setSelectedWaypointIdx(null);
  };

  // Calibration
  const calibratePixelDistance = calibratePoints.length === 2
    ? Math.sqrt(Math.pow(calibratePoints[1].x - calibratePoints[0].x, 2) + Math.pow(calibratePoints[1].y - calibratePoints[0].y, 2))
    : null;

  const applyCalibration = () => {
    const distMm = parseFloat(calibrateDistanceMm);
    if (!calibratePixelDistance || !distMm || distMm <= 0) return;
    setCalibrationData({ point1: calibratePoints[0], point2: calibratePoints[1], distanceMm: distMm, pxPerMm: calibratePixelDistance / distMm });
    setCalibrateMode(false);
    setCalibratePoints([]);
    setCalibrateDistanceMm('');
  };

  const clearCalibration = () => { setCalibrationData(null); setCalibratePoints([]); setCalibrateDistanceMm(''); setCalibrateMode(false); };

  // Waypoint drag
  const handleWaypointDrag = (idx, e) => {
    const pxX = e.target.x();
    const pxY = e.target.y();
    const mmX = Math.round(pxX / pxPerMm);
    const mmY = Math.round(pxY / pxPerMm);
    setWaypoints(prev => prev.map((wp, i) => i === idx ? { ...wp, x_mm: mmX, y_mm: mmY } : wp));
  };

  const toggleReverse = (idx) => {
    setWaypoints(prev => prev.map((wp, i) => i === idx ? { ...wp, reverse: !wp.reverse } : wp));
  };

  const deleteWaypoint = (idx) => {
    setWaypoints(prev => prev.filter((_, i) => i !== idx));
    setSelectedWaypointIdx(null);
  };

  const clearPath = () => { setWaypoints([]); setSelectedWaypointIdx(null); setAnalysisFlags([]); };

  // Swept envelope computation — smooth continuous outline
  const envelopeShape = useMemo(() => {
    if (!selectedVehicle || waypoints.length < 2) return null;
    const halfW = selectedVehicle.width_mm / 2 + 300; // + 300mm safety

    // Build left and right offset points along the path
    const leftPts = [];
    const rightPts = [];

    for (let i = 0; i < waypoints.length; i++) {
      const wp = waypoints[i];

      // Calculate the direction at this waypoint (average of incoming + outgoing)
      let dirX = 0, dirY = 0;
      if (i < waypoints.length - 1) {
        const next = waypoints[i + 1];
        dirX += next.x_mm - wp.x_mm;
        dirY += next.y_mm - wp.y_mm;
      }
      if (i > 0) {
        const prev = waypoints[i - 1];
        dirX += wp.x_mm - prev.x_mm;
        dirY += wp.y_mm - prev.y_mm;
      }
      const len = Math.sqrt(dirX * dirX + dirY * dirY);
      if (len < 0.001) continue;
      const nx = -dirY / len;
      const ny = dirX / len;

      // At turns, widen the envelope based on turning geometry
      let widthMult = 1.0;
      if (i > 0 && i < waypoints.length - 1) {
        const prev = waypoints[i - 1];
        const next = waypoints[i + 1];
        const dx1 = wp.x_mm - prev.x_mm, dy1 = wp.y_mm - prev.y_mm;
        const dx2 = next.x_mm - wp.x_mm, dy2 = next.y_mm - wp.y_mm;
        const l1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
        const l2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
        if (l1 > 0 && l2 > 0) {
          const dot = (dx1 * dx2 + dy1 * dy2) / (l1 * l2);
          const angle = Math.acos(Math.max(-1, Math.min(1, dot)));
          // Widen at turns: up to 1.5x at 90°
          widthMult = 1.0 + (angle / Math.PI) * 0.6;
        }
      }

      const w = halfW * widthMult;
      leftPts.push({ x: (wp.x_mm + nx * w) * pxPerMm, y: (wp.y_mm + ny * w) * pxPerMm });
      rightPts.push({ x: (wp.x_mm - nx * w) * pxPerMm, y: (wp.y_mm - ny * w) * pxPerMm });
    }

    // Add rounded endcaps (semicircles at start and end)
    const addEndcap = (center, leftPt, rightPt, flip) => {
      const pts = [];
      const cx = center.x_mm * pxPerMm, cy = center.y_mm * pxPerMm;
      const startAngle = Math.atan2(leftPt.y - cy, leftPt.x - cx);
      const endAngle = Math.atan2(rightPt.y - cy, rightPt.x - cx);
      const r = Math.sqrt((leftPt.x - cx) ** 2 + (leftPt.y - cy) ** 2);
      let a1 = flip ? endAngle : startAngle;
      let a2 = flip ? startAngle : endAngle;
      if (!flip && a2 > a1) a2 -= Math.PI * 2;
      if (flip && a2 < a1) a2 += Math.PI * 2;
      const steps = 8;
      for (let s = 0; s <= steps; s++) {
        const a = a1 + (a2 - a1) * (s / steps);
        pts.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
      }
      return pts;
    };

    // Build the full polygon: left side → end cap → right side reversed → start cap
    const allPoints = [];
    leftPts.forEach(p => allPoints.push(p.x, p.y));
    if (waypoints.length >= 2) {
      const endCap = addEndcap(waypoints[waypoints.length - 1], leftPts[leftPts.length - 1], rightPts[rightPts.length - 1], false);
      endCap.forEach(p => allPoints.push(p.x, p.y));
    }
    for (let i = rightPts.length - 1; i >= 0; i--) {
      allPoints.push(rightPts[i].x, rightPts[i].y);
    }
    if (waypoints.length >= 2) {
      const startCap = addEndcap(waypoints[0], rightPts[0], leftPts[0], false);
      startCap.forEach(p => allPoints.push(p.x, p.y));
    }

    return allPoints;
  }, [waypoints, selectedVehicle, pxPerMm]);

  // Error/warning counts
  const errorCount = analysisFlags.filter(f => f.level === 'error').length;
  const warningCount = analysisFlags.filter(f => f.level === 'warning').length;

  return (
    <div className="h-screen flex flex-col bg-slate-100 overflow-hidden" data-testid="vehicle-access-planner">
      {/* ─── Toolbar ─── */}
      <div className="h-[52px] bg-white border-b border-slate-200 flex items-center px-3 gap-1 flex-shrink-0 z-10">
        <button onClick={() => { handleSave(); navigate('/vehicle-access'); }} className="h-8 px-2.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex items-center gap-1.5 text-xs font-medium" data-testid="back-btn">
          <ArrowLeft size={15} strokeWidth={1.5} /> Back
        </button>
        <div className="w-px h-6 bg-slate-200 mx-0.5" />
        <span className="text-xs font-semibold text-slate-700 truncate max-w-[180px]" data-testid="project-name">{projectName}</span>
        <div className="w-px h-6 bg-slate-200 mx-0.5" />

        {/* Vehicle selector */}
        <select value={selectedVehicleId || ''} onChange={e => setSelectedVehicleId(e.target.value || null)}
          className="bg-orange-50 text-orange-700 text-xs font-semibold px-2 py-1.5 rounded-lg border border-orange-200 focus:outline-none cursor-pointer max-w-[200px]"
          data-testid="vehicle-select">
          <option value="">Select vehicle...</option>
          {vehicles.map(v => <option key={v.id} value={v.id}>{v.code} — {v.length_mm / 1000}m</option>)}
        </select>
        <div className="w-px h-6 bg-slate-200 mx-0.5" />

        {/* Scale / Calibration */}
        {calibrationData ? (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
            <span className="text-xs font-semibold text-emerald-700">Calibrated</span>
            <button onClick={clearCalibration} className="text-emerald-400 hover:text-red-500"><X size={12} /></button>
          </div>
        ) : (
          <select value={siteScale} onChange={e => setSiteScale(e.target.value)} className="bg-slate-50 text-slate-700 text-xs font-semibold px-2 py-1.5 rounded-lg border border-slate-200 focus:outline-none cursor-pointer" data-testid="scale-select">
            {SCALES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <button onClick={() => { setCalibrateMode(!calibrateMode); setCalibratePoints([]); setPathMode(false); }}
          className={`h-8 px-2.5 rounded-lg flex items-center gap-1.5 text-xs font-medium transition-all ${calibrateMode ? 'text-emerald-600 bg-emerald-50 border border-emerald-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 border border-transparent'}`}
          data-testid="calibrate-btn">
          <Crosshair size={15} strokeWidth={1.5} /> Calibrate
        </button>
        <div className="w-px h-6 bg-slate-200 mx-0.5" />

        {/* Dimensions */}
        <button onClick={() => setShowSetup(!showSetup)} className="h-8 px-2.5 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-100 border border-transparent flex items-center gap-1.5" data-testid="dimensions-btn">
          {siteWidthMm && siteDepthMm ? `${(siteWidthMm / 1000).toFixed(0)}m x ${(siteDepthMm / 1000).toFixed(0)}m` : 'Dimensions'}
        </button>
        <div className="w-px h-6 bg-slate-200 mx-0.5" />

        {/* Path mode */}
        <button onClick={() => { setPathMode(!pathMode); setCalibrateMode(false); }}
          className={`h-8 px-2.5 rounded-lg flex items-center gap-1.5 text-xs font-medium transition-all ${pathMode ? 'text-blue-600 bg-blue-50 border border-blue-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 border border-transparent'}`}
          data-testid="path-mode-btn">
          <Route size={15} strokeWidth={1.5} /> Add Path
        </button>
        <button onClick={clearPath} className="h-8 px-2.5 rounded-lg text-xs font-medium text-slate-400 hover:text-red-500 hover:bg-red-50 border border-transparent flex items-center gap-1.5" data-testid="clear-path-btn">
          <Trash2 size={14} /> Clear
        </button>
        <div className="w-px h-6 bg-slate-200 mx-0.5" />

        {/* Envelope toggle */}
        <button onClick={() => setShowEnvelope(!showEnvelope)}
          className={`h-8 px-2.5 rounded-lg text-xs font-medium flex items-center gap-1.5 ${showEnvelope ? 'text-blue-600' : 'text-slate-400'} hover:bg-slate-100 border border-transparent`}
          data-testid="toggle-envelope-btn">
          {showEnvelope ? <Eye size={14} /> : <EyeOff size={14} />} Envelope
        </button>
        <div className="w-px h-6 bg-slate-200 mx-0.5" />

        {/* Upload */}
        <input type="file" ref={fileInputRef} className="hidden" accept="image/png,image/jpeg,image/jpg" onChange={handleFileUpload} />
        <button onClick={() => fileInputRef.current?.click()} className="h-8 px-2.5 rounded-lg text-xs font-medium text-slate-400 hover:text-slate-600 hover:bg-slate-100 border border-transparent flex items-center gap-1.5" data-testid="upload-btn">
          <Upload size={14} /> Plan
        </button>
        <div className="w-px h-6 bg-slate-200 mx-0.5" />

        {/* Zoom */}
        <button onClick={zoomOut} className="h-8 w-8 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex items-center justify-center" data-testid="zoom-out-btn"><ZoomOut size={15} /></button>
        <span className="text-[10px] text-slate-400 font-mono w-10 text-center">{Math.round(scale * 100)}%</span>
        <button onClick={zoomIn} className="h-8 w-8 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex items-center justify-center" data-testid="zoom-in-btn"><ZoomIn size={15} /></button>
        <button onClick={resetView} className="h-8 w-8 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex items-center justify-center" data-testid="reset-view-btn"><Maximize size={14} /></button>

        <div className="flex-1" />

        {/* Right actions */}
        <button onClick={handleAnalyse} disabled={analysing} className="h-8 px-3 rounded-lg text-xs font-semibold text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200 flex items-center gap-1.5 disabled:opacity-40" data-testid="analyse-btn">
          {analysing ? 'Checking...' : 'Analyse'}
        </button>
        <button onClick={handleExportPdf} className="h-8 px-3 rounded-lg text-xs font-medium text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex items-center gap-1.5" data-testid="export-pdf-btn">
          <FileDown size={14} /> PDF
        </button>
        <button onClick={handleSave} disabled={saving} className="h-8 px-3 rounded-lg text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 flex items-center gap-1.5 disabled:opacity-40" data-testid="save-btn">
          <Save size={14} /> {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      {/* ─── Main Layout ─── */}
      <div className="flex flex-1 overflow-hidden">
        {/* ─── Canvas ─── */}
        <div ref={containerRef} className="flex-1 relative bg-[#f0f2f5] overflow-hidden cursor-crosshair">
          {/* Upload overlay */}
          {showUpload && !planImage && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 backdrop-blur-sm" data-testid="upload-overlay">
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4"><Upload size={28} className="text-orange-500" /></div>
                <h3 className="text-lg font-semibold text-slate-800 mb-1">Upload Site Plan</h3>
                <p className="text-sm text-slate-400 mb-4">PNG or JPG of your site layout</p>
                <div className="flex items-center justify-center gap-3">
                  <button onClick={() => fileInputRef.current?.click()} className="px-5 py-2.5 bg-orange-600 text-white text-sm font-medium rounded-xl hover:bg-orange-700" data-testid="upload-plan-btn">Choose File</button>
                  <button onClick={() => { setShowUpload(false); setShowSetup(true); }} className="px-5 py-2.5 text-slate-500 text-sm hover:text-slate-700" data-testid="skip-upload-btn">Skip — blank canvas</button>
                </div>
              </div>
            </div>
          )}

          {/* Setup panel */}
          {showSetup && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 bg-white border border-slate-200 shadow-lg rounded-xl px-5 py-4 flex items-center gap-3" data-testid="setup-panel">
              <span className="text-xs text-slate-500 font-medium">Site size</span>
              <input type="number" value={siteWidthMm} onChange={e => setSiteWidthMm(e.target.value)} placeholder="Width mm" className="w-24 bg-slate-50 text-slate-700 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 focus:border-blue-400 focus:outline-none" data-testid="site-width-input" />
              <span className="text-slate-300 text-xs">x</span>
              <input type="number" value={siteDepthMm} onChange={e => setSiteDepthMm(e.target.value)} placeholder="Depth mm" className="w-24 bg-slate-50 text-slate-700 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 focus:border-blue-400 focus:outline-none" data-testid="site-depth-input" />
              <button onClick={() => { setShowSetup(false); handleSave(); }} className="text-xs font-semibold text-blue-600 hover:text-blue-700 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 rounded-lg" data-testid="apply-dimensions-btn">Apply</button>
              <button onClick={() => setShowSetup(false)} className="text-slate-400 hover:text-slate-600 text-xs">Cancel</button>
            </div>
          )}

          {/* Calibrate panel */}
          {calibrateMode && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 bg-white border border-emerald-200 shadow-lg rounded-xl px-5 py-4 max-w-md" data-testid="calibrate-panel">
              {calibratePoints.length < 2 ? (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0"><Crosshair size={16} className="text-emerald-600" /></div>
                  <div>
                    <p className="text-xs font-semibold text-slate-800">{calibratePoints.length === 0 ? 'Click the first point on your plan' : 'Click the second point'}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">Choose two points with a known real-world distance</p>
                  </div>
                  <button onClick={() => { setCalibrateMode(false); setCalibratePoints([]); }} className="text-slate-400 hover:text-slate-600 ml-2"><X size={14} /></button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0"><Check size={16} className="text-emerald-600" /></div>
                  <div>
                    <p className="text-xs font-semibold text-slate-800 mb-1.5">Enter the real distance between those points</p>
                    <div className="flex items-center gap-2">
                      <input type="number" value={calibrateDistanceMm} onChange={e => setCalibrateDistanceMm(e.target.value)} placeholder="e.g. 5000"
                        className="w-28 bg-slate-50 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 focus:border-emerald-400 focus:outline-none" data-testid="calibrate-distance-input" autoFocus />
                      <span className="text-[10px] text-slate-400">mm</span>
                      <button onClick={applyCalibration} disabled={!calibrateDistanceMm || parseFloat(calibrateDistanceMm) <= 0}
                        className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-lg disabled:opacity-40" data-testid="apply-calibration-btn">Apply</button>
                    </div>
                  </div>
                  <button onClick={() => { setCalibratePoints([]); setCalibrateDistanceMm(''); }} className="text-slate-400 hover:text-slate-600 ml-2 text-[10px]">Reset</button>
                </div>
              )}
            </div>
          )}

          {/* Mode indicator */}
          <div className="absolute bottom-3 left-3 z-10 flex items-center gap-2">
            {pathMode && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-3 py-1.5 text-xs text-blue-700 font-medium" data-testid="path-mode-indicator">
                Click canvas to add waypoints ({waypoints.length} placed)
              </div>
            )}
            {calibrateMode && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-1.5 text-xs text-emerald-700 font-medium">
                {calibratePoints.length === 0 ? 'Click first point...' : calibratePoints.length === 1 ? 'Click second point...' : 'Enter distance above'}
              </div>
            )}
          </div>

          {/* Konva Stage */}
          <Stage ref={stageRef} width={stageSize.width} height={stageSize.height}
            scaleX={scale} scaleY={scale} x={position.x} y={position.y}
            draggable={!pathMode && !calibrateMode}
            onWheel={handleWheel}
            onDragEnd={(e) => { if (e.target === stageRef.current) setPosition({ x: e.target.x(), y: e.target.y() }); }}
            onClick={handleStageClick}>

            {/* Background image */}
            {planImageObj && (
              <Layer>
                {roomWPx > 0 && roomHPx > 0 ? (
                  <KonvaImage image={planImageObj} x={0} y={0} width={roomWPx} height={roomHPx} opacity={0.4} listening={false} />
                ) : (
                  <KonvaImage image={planImageObj} x={0} y={0} opacity={0.4} listening={false} />
                )}
              </Layer>
            )}

            {/* Site boundary + grid */}
            <Layer>
              {roomWPx > 0 && roomHPx > 0 && (
                <>
                  <Rect x={0} y={0} width={roomWPx} height={roomHPx} stroke="#94a3b8" strokeWidth={1.5 / scale} fill="rgba(248,250,252,0.3)" dash={[8 / scale, 4 / scale]} listening={false} />
                  {/* Grid every 5m */}
                  {Array.from({ length: Math.floor(parseFloat(siteWidthMm) / 5000) }).map((_, i) => {
                    const x = (i + 1) * 5000 * pxPerMm;
                    return <Line key={`gx-${i}`} points={[x, 0, x, roomHPx]} stroke="#e2e8f0" strokeWidth={0.5 / scale} listening={false} />;
                  })}
                  {Array.from({ length: Math.floor(parseFloat(siteDepthMm) / 5000) }).map((_, i) => {
                    const y = (i + 1) * 5000 * pxPerMm;
                    return <Line key={`gy-${i}`} points={[0, y, roomWPx, y]} stroke="#e2e8f0" strokeWidth={0.5 / scale} listening={false} />;
                  })}
                </>
              )}
            </Layer>

            {/* Swept envelope — smooth continuous outline */}
            {showEnvelope && envelopeShape && (
              <Layer>
                <Line points={envelopeShape} closed
                  fill="rgba(59,130,246,0.06)"
                  stroke="rgba(59,130,246,0.5)"
                  strokeWidth={1.5 / scale}
                  listening={false}
                  tension={0.2}
                />
              </Layer>
            )}

            {/* Path lines + waypoints */}
            <Layer>
              {/* Path segments */}
              {waypoints.length >= 2 && waypoints.map((wp, i) => {
                if (i === 0) return null;
                const prev = waypoints[i - 1];
                const isRev = wp.reverse;
                return (
                  <Arrow key={`seg-${i}`}
                    points={[prev.x_mm * pxPerMm, prev.y_mm * pxPerMm, wp.x_mm * pxPerMm, wp.y_mm * pxPerMm]}
                    stroke={isRev ? '#dc2626' : '#2563eb'} strokeWidth={2.5 / scale}
                    dash={isRev ? [6 / scale, 3 / scale] : undefined}
                    pointerLength={8 / scale} pointerWidth={6 / scale}
                    fill={isRev ? '#dc2626' : '#2563eb'}
                    listening={false} />
                );
              })}

              {/* Waypoint markers */}
              {waypoints.map((wp, i) => (
                <Group key={`wp-${i}`} x={wp.x_mm * pxPerMm} y={wp.y_mm * pxPerMm}
                  draggable
                  onDragEnd={(e) => handleWaypointDrag(i, e)}
                  onClick={(e) => { e.cancelBubble = true; setSelectedWaypointIdx(i); }}>
                  <Circle radius={7 / scale}
                    fill={i === selectedWaypointIdx ? '#f97316' : wp.reverse ? '#dc2626' : '#2563eb'}
                    stroke="white" strokeWidth={2 / scale} />
                  <Text text={String(i + 1)} fontSize={9 / scale} fill="white" fontStyle="bold"
                    offsetX={3 / scale} offsetY={4 / scale} listening={false} />
                </Group>
              ))}

              {/* Vehicle outline at first waypoint */}
              {selectedVehicle && waypoints.length >= 1 && (() => {
                const wp = waypoints[0];
                const vLenPx = selectedVehicle.length_mm * pxPerMm;
                const vWidPx = selectedVehicle.width_mm * pxPerMm;
                let angle = 0;
                if (waypoints.length >= 2) {
                  const dx = waypoints[1].x_mm - wp.x_mm;
                  const dy = waypoints[1].y_mm - wp.y_mm;
                  angle = Math.atan2(dy, dx) * 180 / Math.PI;
                }
                return (
                  <Group x={wp.x_mm * pxPerMm} y={wp.y_mm * pxPerMm} rotation={angle}>
                    <Rect x={-selectedVehicle.front_overhang_mm * pxPerMm} y={-vWidPx / 2}
                      width={vLenPx} height={vWidPx}
                      stroke="#f97316" strokeWidth={2 / scale}
                      fill="rgba(249,115,22,0.1)"
                      dash={[4 / scale, 2 / scale]}
                      listening={false} />
                    {/* Cab indicator */}
                    <Rect x={0} y={-vWidPx / 2}
                      width={selectedVehicle.wheelbase_mm * 0.3 * pxPerMm} height={vWidPx}
                      fill="rgba(249,115,22,0.15)" listening={false} />
                    <Text text={selectedVehicle.code} fontSize={10 / scale} fill="#f97316" fontStyle="bold"
                      x={2 / scale} y={-4 / scale} listening={false} />
                  </Group>
                );
              })()}
            </Layer>

            {/* Calibrate line */}
            {calibrateMode && calibratePoints.length > 0 && (
              <Layer>
                {calibratePoints.map((pt, i) => (
                  <Circle key={`cp-${i}`} x={pt.x} y={pt.y} radius={5 / scale} fill="#10b981" stroke="#fff" strokeWidth={1.5 / scale} />
                ))}
                {calibratePoints.length === 2 && (
                  <>
                    <Line points={[calibratePoints[0].x, calibratePoints[0].y, calibratePoints[1].x, calibratePoints[1].y]}
                      stroke="#10b981" strokeWidth={2 / scale} dash={[6 / scale, 3 / scale]} />
                    <Text x={(calibratePoints[0].x + calibratePoints[1].x) / 2} y={(calibratePoints[0].y + calibratePoints[1].y) / 2 - 18 / scale}
                      text={calibrateDistanceMm ? `${calibrateDistanceMm}mm` : `${Math.round(calibratePixelDistance)}px`}
                      fontSize={12 / scale} fill="#10b981" fontStyle="bold" align="center" offsetX={30 / scale} width={60 / scale} />
                  </>
                )}
              </Layer>
            )}
          </Stage>
        </div>

        {/* ─── Right: Analysis Panel ─── */}
        <div className="w-80 bg-white/95 backdrop-blur-md border-l border-slate-200 flex flex-col flex-shrink-0 overflow-hidden" data-testid="analysis-panel">
          <div className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800">Analysis</h3>
            {(errorCount > 0 || warningCount > 0) && (
              <div className="flex items-center gap-1.5">
                {errorCount > 0 && <span className="text-[10px] px-2 py-0.5 bg-red-50 text-red-500 rounded-full font-semibold">{errorCount}</span>}
                {warningCount > 0 && <span className="text-[10px] px-2 py-0.5 bg-amber-50 text-amber-600 rounded-full font-semibold">{warningCount}</span>}
              </div>
            )}
          </div>

          {/* Vehicle info */}
          {selectedVehicle && (
            <div className="px-5 py-3 border-b border-slate-100 bg-orange-50/50">
              <div className="flex items-center gap-2 mb-2">
                <Truck size={14} className="text-orange-500" />
                <span className="text-xs font-semibold text-slate-800">{selectedVehicle.name}</span>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                <span className="text-slate-400">Length</span><span className="text-slate-700 font-medium">{selectedVehicle.length_mm / 1000}m</span>
                <span className="text-slate-400">Width</span><span className="text-slate-700 font-medium">{selectedVehicle.width_mm / 1000}m</span>
                <span className="text-slate-400">Turn radius</span><span className="text-slate-700 font-medium">{selectedVehicle.min_turning_radius_mm / 1000}m</span>
                <span className="text-slate-400">Swept circle</span><span className="text-slate-700 font-medium">{selectedVehicle.swept_circle_mm / 1000}m</span>
              </div>
            </div>
          )}

          {/* Waypoint list */}
          <div className="px-5 py-3 border-b border-slate-100">
            <h4 className="text-xs font-semibold text-slate-600 mb-2">Path Waypoints ({waypoints.length})</h4>
            {waypoints.length === 0 ? (
              <p className="text-[11px] text-slate-400">Click "Add Path" then click on the canvas to place waypoints.</p>
            ) : (
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {waypoints.map((wp, i) => (
                  <div key={i} className={`flex items-center gap-2 px-2 py-1 rounded-lg text-[11px] cursor-pointer ${i === selectedWaypointIdx ? 'bg-orange-50 border border-orange-200' : 'hover:bg-slate-50'}`}
                    onClick={() => setSelectedWaypointIdx(i)} data-testid={`waypoint-${i}`}>
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center text-white text-[9px] font-bold ${wp.reverse ? 'bg-red-500' : 'bg-blue-500'}`}>{i + 1}</span>
                    <span className="text-slate-600 flex-1">{(wp.x_mm / 1000).toFixed(1)}m, {(wp.y_mm / 1000).toFixed(1)}m</span>
                    {i > 0 && (
                      <button onClick={(e) => { e.stopPropagation(); toggleReverse(i); }}
                        className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${wp.reverse ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'}`}
                        data-testid={`toggle-reverse-${i}`}>
                        {wp.reverse ? 'REV' : 'FWD'}
                      </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); deleteWaypoint(i); }} className="text-slate-300 hover:text-red-500"><Trash2 size={11} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Analysis flags */}
          <div className="flex-1 overflow-y-auto px-5 py-3">
            {analysisFlags.length === 0 ? (
              <p className="text-[11px] text-slate-400 text-center mt-4">Click "Analyse" to check vehicle access compliance.</p>
            ) : (
              <div className="space-y-2">
                {analysisFlags.map((flag, i) => {
                  const colors = { error: 'bg-red-50 border-red-100 text-red-700', warning: 'bg-amber-50 border-amber-100 text-amber-700', success: 'bg-emerald-50 border-emerald-100 text-emerald-700', info: 'bg-slate-50 border-slate-100 text-slate-600' };
                  return (
                    <div key={i} className={`px-3 py-2 rounded-lg border text-[11px] leading-relaxed ${colors[flag.level] || colors.info}`} data-testid={`flag-${i}`}>
                      {flag.message}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Disclaimer */}
          <div className="px-5 py-3 border-t border-slate-100 bg-red-50/50">
            <p className="text-[9px] text-red-400 leading-relaxed">
              Preliminary design tool only. Professional traffic engineering assessment is required for final DA submission.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleAccessPlanner;
